import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { ChevronDown, Check } from "lucide-react"
import { cn } from "@/lib/utils"

const Select = SelectPrimitive.Root
const SelectValue = SelectPrimitive.Value
const SelectGroup = SelectPrimitive.Group

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex items-center justify-between gap-2 w-full h-[34px] px-[11px]",
      "text-[13px] text-[var(--text-primary)] bg-[var(--bg-elevated)]",
      "border border-[var(--border-subtle-default)] rounded-[var(--radius-md)]",
      "transition-[border-color] duration-[time:80ms] outline-none",
      "hover:border-[var(--border-subtle-strong)]",
      "focus:border-[var(--border-subtle-focus)] focus:shadow-[0_0_0_3px_var(--accent-glow)]",
      "data-[placeholder]:text-[var(--text-disabled)]",
      "disabled:opacity-40 disabled:cursor-not-allowed",
      className
    )}
    {...props}
  >
    {children}
    <ChevronDown size={13} strokeWidth={1.5} className="text-[var(--text-muted)] ml-auto flex-shrink-0" />
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = "SelectTrigger"

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "z-50 min-w-[180px] p-1",
        "bg-[var(--bg-elevated)] border border-[var(--border-subtle-default)]",
        "rounded-[var(--radius-lg)] shadow-[var(--shadow-md)]",
        "data-[state=open]:animate-fade-in",
        className
      )}
      {...props}
    >
      <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = "SelectContent"

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "flex items-center gap-2 h-8 px-[10px] text-[13px] text-[var(--text-secondary)]",
      "rounded-[var(--radius-md)] cursor-pointer outline-none transition-colors duration-[time:80ms]",
      "hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]",
      "focus:bg-[var(--bg-base)] focus:text-[var(--text-primary)]",
      "data-[state=checked]:text-[var(--text-primary)]",
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="ml-auto">
      <Check size={11} strokeWidth={2} />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
))
SelectItem.displayName = "SelectItem"

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-[10px] py-1 text-[11px] text-[var(--text-muted)] font-medium uppercase tracking-wider", className)}
    {...props}
  />
))
SelectLabel.displayName = "SelectLabel"

export { Select, SelectValue, SelectTrigger, SelectContent, SelectItem, SelectLabel, SelectGroup }
